import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const menuItems: Array<{
  label: string;
  href: string;
  external?: boolean;
  badge?: string;
}> = [
  { label: "Platform", href: "/#platform" },
  { label: "Enterprise", href: "/#enterprise" },
  { label: "How it Works", href: "/#how-it-works" },
  // { label: "Pricing", href: "/#pricing", badge: "Soon" },
  { label: "Docs", href: "https://docs.vokaris.io", external: true },
];

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when scrolling
  useEffect(() => {
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [isScrolled]);

  return (
    <motion.nav
      initial={false}
      animate={{
        top: isScrolled ? 16 : 0,
        width: isScrolled ? "calc(100% - 2rem)" : "100%",
        maxWidth: isScrolled ? "56rem" : "100%",
        borderRadius: isScrolled ? 9999 : 0,
      }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 30,
      }}
      className={`fixed z-50 left-1/2 -translate-x-1/2 backdrop-blur-md ${
        isScrolled
          ? "bg-white/95 border border-gray-200 shadow-lg shadow-black/5"
          : "bg-white/90 border-b border-gray-100"
      }`}
    >
      <motion.div
        initial={false}
        animate={{
          height: isScrolled ? 56 : 80,
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 30,
        }}
        className="mx-auto flex justify-between items-center px-6"
      >
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <motion.img
            src="/logo-main.svg"
            initial={false}
            animate={{
              height: isScrolled ? 20 : 32,
            }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
            }}
            alt="Vokaris Logo"
          />
        </Link>

        <div className="hidden lg:flex items-center gap-8 text-sm font-medium text-vokaris-teal/70">
          {menuItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              {...(item.external && {
                target: "_blank",
                rel: "noopener noreferrer",
              })}
              className="hover:text-vokaris-teal transition flex items-center gap-1.5"
            >
              {item.label}
              {item.badge && (
                <span className="text-[10px] font-semibold bg-vokaris-green/20 text-vokaris-teal px-1.5 py-0.5 rounded-full">
                  {item.badge}
                </span>
              )}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <a
            href="https://hub.vokaris.io"
            className="hidden sm:block bg-vokaris-teal text-white px-5 py-2.5 rounded-2xl text-sm font-bold hover:bg-vokaris-green hover:text-vokaris-teal transition duration-300"
          >
            Start deploying →
          </a>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-vokaris-teal"
            aria-label="Toggle menu"
          >
            <motion.div
              initial={false}
              animate={{ rotate: isMobileMenuOpen ? 90 : 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
            >
              {isMobileMenuOpen ? (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </motion.div>
          </button>
        </div>
      </motion.div>

      {/* Mobile menu dropdown */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{
              type: "spring",
              stiffness: 400,
              damping: 30,
            }}
            className="lg:hidden absolute top-full left-0 right-0 mt-2 mx-4 bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden"
          >
            <div className="flex flex-col p-4 gap-1">
              {menuItems.map((item, index) => (
                <motion.a
                  key={item.href}
                  href={item.href}
                  {...(item.external && {
                    target: "_blank",
                    rel: "noopener noreferrer",
                  })}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    delay: index * 0.05,
                    type: "spring",
                    stiffness: 400,
                    damping: 30,
                  }}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="px-4 py-3 text-vokaris-teal/70 hover:text-vokaris-teal hover:bg-gray-50 rounded-lg transition font-medium flex items-center gap-2"
                >
                  {item.label}
                  {item.badge && (
                    <span className="text-[10px] font-semibold bg-vokaris-green/20 text-vokaris-teal px-1.5 py-0.5 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </motion.a>
              ))}
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  delay: menuItems.length * 0.05,
                  type: "spring",
                  stiffness: 400,
                  damping: 30,
                }}
                className="border-t border-gray-100 mt-2 pt-2"
              >
                <a
                  href="https://hub.vokaris.io"
                  className="block px-4 py-3 bg-vokaris-teal text-white text-center rounded-2xl font-bold hover:bg-vokaris-green hover:text-vokaris-teal transition duration-300"
                >
                  Start deploying →
                </a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
